import React from 'react';
import PropTypes from 'prop-types';
import StructuredContent from '@nerdwallet/structured-content';
import {
  stringInputSpec,
  boolCheckboxSpec,
  stringSelectSpec,
  scBackgroundColorPicker,
} from '../../lib/editing-configs';

const createCTAComponentProps = ({
  ctaComponent,
  CTAButtonAnnotation,
  CTAButtonText,
  CTAZipCode,
  CTAInternalExperience,
  CTAPricingCode,
  CTAButtonLink,
  CTAMarketplaceID,
}) => {
  switch (ctaComponent) {
    case 'AutoInsuranceEntryPointForm':
    case 'HomeInsuranceEntryPointForm':
      return {
        buttonAnnotation: CTAButtonAnnotation,
        buttonText: CTAButtonText,
        defaultZipCode: CTAZipCode,
        isInternal: CTAInternalExperience,
        pricingCode: CTAPricingCode,
      };
    case 'Button':
      return {
        href: CTAButtonLink,
        children: CTAButtonText,
        buttonAnnotation: CTAButtonAnnotation,
      };
    case 'MarketplaceApplyNow':
      return {
        id: CTAMarketplaceID,
      };
    default:
      return {};
  }
};

const ArticleHeroWrapper = ({
  title,
  subTitle,
  eyebrowText,
  imageSrc,
  imageAlt,
  backgroundColor,
  showAdvertiserDisclosure,
  ctaComponent,
  halfHeight,
  ...props
}) => {
  const ctaComponentProps = createCTAComponentProps({
    ctaComponent,
    ...props,
  });

  return (
    <StructuredContent>
      {{
        type: 'ArticleHero',
        version: '1.0.0',
        props: {
          title,
          subTitle,
          eyebrowText,
          ctaComponent,
          imageData: {
            alt: imageAlt,
            src: imageSrc,
          },
          ctaComponentProps,
          backgroundColor,
          showAdvertiserDisclosure,
          halfHeight,
        },
      }}
    </StructuredContent>
  );
};

ArticleHeroWrapper.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  eyebrowText: PropTypes.string,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  backgroundColor: PropTypes.string,
  showAdvertiserDisclosure: PropTypes.bool,
  ctaComponent: PropTypes.string,
  CTAButtonAnnotation: PropTypes.string,
  CTAButtonText: PropTypes.string,
  CTAButtonLink: PropTypes.string,
  CTAZipCode: PropTypes.string,
  CTAInternalExperience: PropTypes.bool,
  CTAPricingCode: PropTypes.string,
  CTAMarketplaceID: PropTypes.string,
  halfHeight: PropTypes.bool,
};

ArticleHeroWrapper.defaultProps = {
  title: 'Article Hero title',
  subTitle: 'Article Hero subtitle',
  imageSrc: 'https://picsum.photos/id/24/485/485',
  imageAlt: 'Book',
  ctaComponent: 'AutoInsuranceEntryPointForm',
  showAdvertiserDisclosure: true,
  backgroundColor: 'green-lightest',
};

ArticleHeroWrapper.getEditingConfig = () => {
  return [
    stringInputSpec('title', 'Hero Title'),
    stringInputSpec('subTitle', 'Hero Subtitle'),
    stringInputSpec('eyebrowText', 'Hero Preheader', {
      help: `a.k.a eyebrow text`,
    }),
    stringInputSpec('imageSrc', 'Hero Image SRC'),
    stringInputSpec('imageAlt', 'Hero Image Alt'),
    scBackgroundColorPicker,
    boolCheckboxSpec('showAdvertiserDisclosure', 'Show Advertiser Disclosure'),
    boolCheckboxSpec('halfHeight', 'Half Height'),
    stringSelectSpec('ctaComponent', 'Hero CTA - Component', [
      {
        label: 'Auto Insurance Entry Point',
        value: 'AutoInsuranceEntryPointForm',
      },
      {
        label: 'Home Insurance Entry Point',
        value: 'HomeInsuranceEntryPointForm',
      },
      { label: 'Button', value: 'Button' },
      { label: 'Marketplace Apply Now', value: 'MarketplaceApplyNow' },
    ]),
    stringInputSpec('CTAButtonAnnotation', 'Hero CTA - Button Annotation'),
    stringInputSpec('CTAButtonLink', 'Hero CTA - Button Link'),
    stringInputSpec('CTAButtonText', 'Hero CTA - Button Text'),
    stringInputSpec('CTAZipCode', 'Hero CTA - Default Zipcode'),
    boolCheckboxSpec('CTAInternalExperience', 'Use NW Internal Experience?'),
    stringInputSpec('CTAPricingCode', 'Hero CTA - Pricing code'),
    stringInputSpec('CTAMarketplaceID', 'Hero CTA - Marketplace Entity'),
  ];
};

ArticleHeroWrapper.displayName = 'ArticleHeroWrapper';

export default ArticleHeroWrapper;
